import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Edit, Trash2, Eye, Mail, Crown, FileText, CheckCircle } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandling';
import KebabMenu from '../ui/KebabMenu';

const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      // For now, we'll show placeholder data since the API might not be implemented yet
      setTeamMembers([
        {
          id: '1',
          name: 'John Smith',
          email: 'john.smith@bayesprep.com',
          role: 'CONTENT_SUPERVISOR',
          status: 'Active',
          questionsCreated: 0,
          questionsReviewed: 45,
          lastActive: '2024-01-16T10:30:00Z',
          joinedAt: '2024-01-01T09:00:00Z'
        },
        {
          id: '2',
          name: 'Sarah Johnson',
          email: 'sarah.johnson@bayesprep.com',
          role: 'CONTENT_GENERATOR',
          status: 'Active',
          questionsCreated: 123,
          questionsReviewed: 0,
          lastActive: '2024-01-16T14:20:00Z',
          joinedAt: '2024-01-05T11:30:00Z'
        },
        {
          id: '3',
          name: 'Mike Davis',
          email: 'mike.davis@bayesprep.com',
          role: 'CONTENT_REVIEWER',
          status: 'Active',
          questionsCreated: 0,
          questionsReviewed: 89,
          lastActive: '2024-01-15T16:45:00Z',
          joinedAt: '2024-01-08T13:15:00Z'
        }
      ]);
    } catch (error) {
      console.error('Error loading team members:', error);
      showErrorToast(error, 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = () => {
    showSuccessToast('Create team member functionality coming soon!');
  };

  const handleEditMember = (member) => {
    console.log('Edit member:', member);
    showSuccessToast('Edit team member functionality coming soon!');
  };

  const handleDeleteMember = async (member) => {
    if (window.confirm(`Are you sure you want to remove ${member.name} from the team?`)) {
      try {
        // await superAdminAPI.deleteTeamMember(member.id);
        showSuccessToast('Team member removed successfully!');
        loadTeamMembers();
      } catch (error) {
        console.error('Error removing team member:', error);
        showErrorToast(error, 'Failed to remove team member');
      }
    }
  };

  const handleViewMember = (member) => {
    console.log('View member:', member);
    showSuccessToast('View team member details functionality coming soon!');
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'CONTENT_SUPERVISOR': { label: 'Content Supervisor', class: 'bg-purple-100 text-purple-800', icon: Crown },
      'CONTENT_GENERATOR': { label: 'Content Generator', class: 'bg-blue-100 text-blue-800', icon: FileText },
      'CONTENT_REVIEWER': { label: 'Content Reviewer', class: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    return roleConfig[role] || { label: role, class: 'bg-gray-100 text-gray-800', icon: Users };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-red-100 text-red-800',
      'Pending': 'bg-yellow-100 text-yellow-800'
    };
    return statusConfig[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !selectedRole || member.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'CONTENT_SUPERVISOR', label: 'Content Supervisor' },
    { value: 'CONTENT_GENERATOR', label: 'Content Generator' },
    { value: 'CONTENT_REVIEWER', label: 'Content Reviewer' }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600 mt-1">Manage content team members and their roles</p>
        </div>
        <button
          onClick={handleCreateMember}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Team Member
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search team members by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {roleOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <CardLoadingSpinner />
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No team members found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedRole ? 'Try adjusting your search criteria or filters.' : 'Add your first team member to get started.'}
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreateMember}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Team Member
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMembers.map((member) => {
                  const roleConfig = getRoleBadge(member.role);
                  const RoleIcon = roleConfig.icon;
                  return (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {member.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {member.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${roleConfig.class}`}>
                          <RoleIcon className="h-3 w-3 mr-1" />
                          {roleConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(member.status)}`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {member.questionsCreated > 0 && (
                            <div>Created: {member.questionsCreated}</div>
                          )}
                          {member.questionsReviewed > 0 && (
                            <div>Reviewed: {member.questionsReviewed}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {new Date(member.lastActive).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <KebabMenu
                          items={[
                            {
                              label: 'View Details',
                              icon: Eye,
                              onClick: () => handleViewMember(member)
                            },
                            {
                              label: 'Edit Member',
                              icon: Edit,
                              onClick: () => handleEditMember(member)
                            },
                            {
                              label: 'Remove Member',
                              icon: Trash2,
                              onClick: () => handleDeleteMember(member),
                              className: 'text-red-600 hover:text-red-800'
                            }
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
